import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'
import { eventBus } from '@/main'
import isEqual from 'lodash/isEqual'

// action types
export const UPDATE_RESELLER_IP_LIMITS = 'updateResellerIpLimits'
export const GET_RESELLER_API_KEY = 'getResellerApiKey'
export const REGENERATE_RESELLER_API_KEY = 'regenerateResellerApiKey'

// mutation types
export const SAVE_RESELLER_API_KEY = 'saveResellerApiKey'
export const CLEAR_RESELLER_API_KEY = 'clearResellerApiKey'
export const SAVE_RESELLER_IP_LIMITS = 'saveResellerIpLimits'
export const CLEAR_RESELLER_IP_LIMITS = 'clearResellerIpLimits'
export const SAVE_RESELLER_IP_LIMITS_SNAPSHOT = 'saveResellerIpLimitsSnapshot'
export const CLEAR_RESELLER_IP_LIMITS_SNAPSHOT = 'clearResellerIpLimitsSnapshot'

const SET_RESPONSE_STATE = 'setResponseState'
export const TOAST_RESELLER = 'showToast/ResellerApi'

const state = {
  resellerApiKey: '',
  resellerIpLimits: [],
  resellerIpLimitsSnapshot: [],

  responses: {
    getResellerApi: false,
    regenerateResellerApiKey: false,
    updateResellerIpLimits: false,
  }
}

const getters = {
  resellerApiKey(state) {
    return state.resellerApiKey
  },
  resellerApiKeyExist(state) {
    return state.resellerApiKey.length > 0
  },
  resellerIpLimits(state) {
    return state.resellerIpLimits
  },
  resellerIpLimitsLength(state) {
    return state.resellerIpLimits.length
  },
  resellerIpLimitsEmpty(state) {
    return state.resellerIpLimits.length == 0
  },
  resellerIpLimitsSnapshot(state) {
    return state.resellerIpLimitsSnapshot
  },
  resellerIpLimitsChanged(state) {
    return !isEqual(state.resellerIpLimits, state.resellerIpLimitsSnapshot)
  },
  resellerJobsVocabulary(state) {
    return state.resellerJobsVocabulary
  },
  resellerJobTitle: state => type => {
    const target = state.resellerJobsVocabulary.find(job => job.type === type)
    if (!target) {
      return target.type
    }
    return target.title
  },
  resellerResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [UPDATE_RESELLER_IP_LIMITS](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'updateResellerIpLimits', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.put(
          `${process.env.VUE_APP_RESELLER}/${id}/api/limit`,
          { ip: context.getters.resellerIpLimits }
        )
          .then(({ data }) => {
            eventBus.$emit(TOAST_RESELLER, {
              message: data.message,
              variant: 'info'
            })
            context.commit(SAVE_RESELLER_IP_LIMITS_SNAPSHOT, context.state.resellerIpLimits)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_RESELLER, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'updateResellerIpLimits', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_RESELLER_API_KEY](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getResellerApi', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RESELLER}/${id}/api`)
          .then(({ data }) => {
            context.commit(SAVE_RESELLER_API_KEY, data.api)
            context.commit(SAVE_RESELLER_IP_LIMITS, data.ip)
            context.commit(SAVE_RESELLER_IP_LIMITS_SNAPSHOT, data.ip)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_RESELLER, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getResellerApi', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [REGENERATE_RESELLER_API_KEY](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'regenerateResellerApiKey', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.post(`${process.env.VUE_APP_RESELLER}/${id}/api`)
          .then(({ data }) => {
            context.commit(SAVE_RESELLER_API_KEY, data.api)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_RESELLER, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'regenerateResellerApiKey', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_RESELLER_API_KEY](state, data) {
    state.resellerApiKey = data
  },
  [CLEAR_RESELLER_API_KEY](state) {
    state.resellerApiKey = ''
  },
  [SAVE_RESELLER_IP_LIMITS](state, data) {
    state.resellerIpLimits = data
  },
  [CLEAR_RESELLER_IP_LIMITS](state) {
    state.resellerIpLimits = []
  },
  [SAVE_RESELLER_IP_LIMITS_SNAPSHOT](state, data) {
    state.resellerIpLimitsSnapshot = data
  },
  [CLEAR_RESELLER_IP_LIMITS_SNAPSHOT](state) {
    state.resellerIpLimitsSnapshot = []
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
